/**
 * What's new → Show me again (#1838). Anyone can replay an intro card: the
 * account menu lists every card for their role, newest first. A replay
 * doesn't change what's saved — it's just the card again.
 */
import { useState } from "react";
import { Sparkles } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { FEATURES, type TeachFeature } from "./features";
import { TeachCard } from "./teach-card";
import { useTeach } from "./teach-state";

export function useWhatsNewAvailable(): boolean {
    return useTeach().state.on;
}

export function WhatsNewDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const { role, state, setTipsOff } = useTeach();
    const [replay, setReplay] = useState<TeachFeature | null>(null);
    const cards = FEATURES.filter((f) => f.intro && f.roles.includes(role)).sort((a, b) =>
        b.shipped.localeCompare(a.shipped),
    );
    return (
        <>
            <Dialog open={open && !replay} onClose={onClose} title="What's new">
                <ul className="m-0 grid list-none gap-2 p-4" data-testid="whats-new">
                    {cards.map((f) => (
                        <li
                            key={f.key}
                            className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2"
                        >
                            <span className="min-w-0">
                                <span className="block font-semibold">{f.intro!.title}</span>
                                <span className="text-xs text-muted-foreground">
                                    {state.seen[f.key] ? "Seen" : "Not seen yet"}
                                </span>
                            </span>
                            <button
                                type="button"
                                onClick={() => setReplay(f)}
                                className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                            >
                                <Sparkles className="h-3.5 w-3.5" aria-hidden />
                                Show me
                            </button>
                        </li>
                    ))}
                    {role === "office" && (
                        <li className="flex items-center justify-between gap-3 px-1 pt-2">
                            <span className="text-sm">Gold dots on buttons you haven&apos;t tried</span>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={!state.tipsOff}
                                onClick={() => setTipsOff(!state.tipsOff)}
                                className="rounded-md border border-border bg-card px-3 py-1.5 text-sm font-semibold hover:bg-muted"
                                data-testid="whats-new-tips"
                            >
                                {state.tipsOff ? "Off — turn on" : "On — turn off"}
                            </button>
                        </li>
                    )}
                </ul>
            </Dialog>
            {replay && (
                <TeachCard feature={replay} replay onGotIt={() => setReplay(null)} onLater={() => setReplay(null)} />
            )}
        </>
    );
}
